import React from 'react';
import { AMBER, GREEN } from '../organizerSpace';

/**
 * Die kleinen, wiederkehrenden Teile der Pflege-Ansicht.
 *
 * Sie stehen hier, weil alle vier Reiter sie brauchen und keiner von ihnen ihr Zuhause ist —
 * vorher lagen sie oben in `OrganizerManage`, zusammen mit allem anderen, was diese Datei auf
 * rund 950 Zeilen brachte. Verändert wurde beim Umzug nichts.
 */

export const STATUS_LABELS: Record<string, string> = {
  PUBLISHED: 'Veröffentlicht',
  DRAFT: 'Entwurf',
  ARCHIVED: 'Archiviert',
};

export const EDIT_STATUS_LABELS: Record<string, string> = {
  PENDING: 'In Prüfung',
  APPROVED: 'Übernommen',
  REJECTED: 'Abgelehnt',
  // In einer anderen Änderung aufgegangen: ersetzt oder mit ihr übernommen (V182).
  MERGED: 'Zusammengeführt',
};

export const EDIT_STATUS_COLORS: Record<string, string> = {
  PENDING: AMBER,
  APPROVED: GREEN,
  REJECTED: '#9A9494',
  MERGED: '#6A6A6A',
};

/**
 * Schloss als gefüllte Marke — für Listen gesperrter Leistungen.
 *
 * Das dünne Strich-Schloss [LockIcon] funktioniert *neben* einer Beschriftung auf einem Knopf,
 * wo der Text die Aussage trägt. In einer Liste muss das Zeichen sie allein tragen: auf 13px
 * verliert die Kontur ihre Form und liest sich als beliebiges Symbol. Deshalb hier eine gefüllte
 * Scheibe in der Markenfarbe mit hellem Schloss darin — dieselbe Farbe wie alle Premium-Haken,
 * aber mit dem Gewicht, das ein Zustand braucht.
 */
export function LockBadge() {
  return (
    <span className="vb-lockbadge" aria-hidden>
      {/*
        12px in einem 20px-Kreis, nicht 11 — beide Ränder sind dann 4px breit und liegen auf
        ganzen Pixeln. Bei 11px waren es 4,5px: der Browser rundet oben/links anders als
        unten/rechts, und das Schloss saß sichtbar schief, obwohl die Zentrierung stimmte.

        Die Form ist aus zwei einfachen Teilen gebaut, damit die Symmetrie nachrechenbar bleibt:
        Körper x 5…19 (Mitte 12), Bügel x 8,5…15,5 (Mitte 12). Senkrecht reicht die Zeichnung von
        3 (Bügeloberkante inkl. halber Strichbreite) bis 21 — Mitte 12. Der frühere Pfad lag
        zwischen 1,8 und 21,8 und damit selbst schon leicht zu hoch.
      */}
      <svg viewBox="0 0 24 24" width="12" height="12">
        <path d="M8.5 10.5V7.5a3.5 3.5 0 0 1 7 0v3" fill="none" stroke="currentColor" strokeWidth="2" />
        <rect x="5" y="10" width="14" height="11" rx="2.2" fill="currentColor" />
      </svg>
    </span>
  );
}

/**
 * Haken an einer erledigten Sache — das Gegenstück zu [LockBadge].
 *
 * Er ersetzt die Wortmarke „STEHT" (02.09.2026): eine grüne Pille mit einem Wort darin sah aus
 * wie eine Beschriftung, die man lesen muss, obwohl die Auskunft binär ist. Der Zustand daneben
 * heißt weiterhin „Fehlt" — dort *ist* das Wort die Auskunft, und ein durchgestrichener Kreis
 * wäre eine zweite Form für dasselbe Ja/Nein.
 *
 * Das Wort bleibt als `aria-label` erhalten: ein Vorlesegerät liest sonst nichts.
 */
export function CheckBadge({ label }: { label: string }) {
  return (
    <span className="vb-checkbadge" role="img" aria-label={label}>
      {/* Dieselbe Geometrie wie am Schloss: 12px Zeichnung in einer 20px-Scheibe, damit die
          beiden Zustaende in einer Liste gleich gross wirken. */}
      <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor"
        strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 12.5l4.8 4.8L19 6.8" />
      </svg>
    </span>
  );
}

/** Schloss an gesperrten Aktionen — dieselbe Marke wie an allen Premium-Haken der Seite. */
export function LockIcon() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.1"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="4" y="10.5" width="16" height="10" rx="2" />
      <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
    </svg>
  );
}

/** Zustands-Marke in der Utility-Schrift — dieselbe Form wie die Chips im Profil. */
export function Chip({ text, tone }: { text: string; tone: string }) {
  return (
    <span className="vb-chip" style={{ color: tone, background: `${tone}1F` }}>
      {text}
    </span>
  );
}

/**
 * Der Weg zu Premium — als **ein** Knopf, der auch weiß, wann es nichts mehr zu bestellen gibt.
 *
 * Liegt schon eine unbezahlte Bestellung bei uns, trägt er den Zustand und ist tot: eine zweite Bestellung
 * weist der Server ohnehin ab (`countOpenVerificationsForOrganizer`), Premium gilt fürs ganze
 * Profil. Der Haken sitzt an vier Stellen (Titelbild, Termin, Statistiken, Verkaufskarte); jede
 * für sich zu prüfen hieße, dass eine davon irgendwann nicht mitzieht und jemand ein zweites
 * Mal bestellt.
 *
 * Die Beschriftung ist dieselbe wie die Pille im Pflege-Balken darüber — derselbe Zustand soll
 * nicht an zwei Stellen zwei Namen haben.
 */
export function PremiumCta({
  requested, onClick, className = 'vb-btn vb-btn--primary', children,
}: {
  requested?: boolean;
  onClick: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <button type="button" className={className} onClick={onClick} disabled={requested}>
      {requested ? 'Zahlung ausstehend' : children}
    </button>
  );
}
